// TODO: Consolidate address fields handling.
// The current AI-generated address handling needs refinement.
// Consider implementing a dedicated formatter that groups and formats preview items based on their field types.
// This approach would keep the payment method form configuration simple while allowing flexible customization on the preview page.

import { format, isValid } from "date-fns";
import { type TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CardContainer } from "@/components/card-container";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LegalDocumentsAcknowledgement } from "@/features/legal";
import {
  type ExtendedPaymentMethod,
  type ExtendedForm,
  type ExtendedProperty,
} from "@/features/payment-methods/hooks/use-available-payment-methods";

import { usePaymentMethodForms } from "../../contexts/use-payment-method-forms";
import { getFieldIcon as getIconFromUtils } from "../../helpers/field-icons";
import {
  type ClientType,
  type FieldTypes,
  isDebitCard,
} from "../../types/available-payment-methods";

// Helper function to get icon for a field type
const getFieldIcon = (
  fieldName: FieldTypes,
  fieldType: ClientType
): React.ReactElement => {
  return getIconFromUtils(fieldName, fieldType);
};

// Helper function to format field label
const formatFieldLabel = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

// Helper function to determine if a field is an address field
const isAddressField = (fieldType: FieldTypes): boolean => {
  const addressTypes = ["line_1", "city", "state", "zip"];
  return addressTypes.includes(fieldType);
};

const EXCLUDED_PREVIEW_TYPES = ["search"];

interface Data {
  value: string;
  type: string;
  label: string;
}
type GroupedData = Record<string, Data>;
type OptionsMap = Record<string, string>;
interface Field {
  type: string;
  label: string;
  optionsMap: OptionsMap | null;
}
type InfoField = Record<string, Field>;
type FormData = Record<string, string>;

// Modify the groupAddressFields function to filter out excluded types
const groupAddressFields = (
  formData: FormData,
  paymentMethod: ExtendedPaymentMethod,
  t: TFunction
): GroupedData => {
  const addressValues: string[] = [];
  const result: GroupedData = {};

  // Get field types and labels from payment method forms including all nested forms
  const getFieldInfo = () => {
    const info: InfoField = {};

    // Process the main form
    const processForm = (form: ExtendedForm) => {
      // Process fields in the current form
      form.fields.forEach((field: ExtendedProperty) => {
        info[field.property_name] = {
          type: field.property_name,
          label: field.title || field.property_name,
          optionsMap:
            field.options && field.options.length > 0
              ? field.options.reduce((acc, item) => {
                  acc[item.key] = item.value;
                  return acc;
                }, {} as OptionsMap)
              : null,
        };
      });

      // Recursively process nested forms
      if (form.next) {
        processForm(form.next);
      }
    };

    // Start processing from the main form
    processForm(paymentMethod.form);

    return info;
  };

  const fieldInfo = getFieldInfo();

  const getFieldValue = (field: Field, value: string) => {
    if (field.type === "date_of_birth" && isValid(new Date(value))) {
      return format(new Date(value), "P");
    }

    if (
      field.type === "card_number" &&
      isDebitCard(paymentMethod.payment_type)
    ) {
      return "•••• •••• •••• ••••";
    }

    if (field.type === "expiration_date") {
      return value;
    }

    if (field.optionsMap) {
      return field.optionsMap[value] || value;
    }

    return value;
  };

  // Process each form data entry
  Object.entries(formData).forEach(([key, value]) => {
    const fieldType = fieldInfo[key]?.type;
    const fieldLabel = fieldInfo[key]?.label;

    // Skip if field info not found or excluded field types
    if (!fieldType || EXCLUDED_PREVIEW_TYPES.includes(fieldType)) {
      return;
    }

    if (isAddressField(fieldType as FieldTypes)) {
      addressValues.push(getFieldValue(fieldInfo[key], value));
    } else {
      result[key] = {
        value: getFieldValue(fieldInfo[key], value),
        type: fieldType,
        label: fieldLabel || formatFieldLabel(key), // Fallback to formatted key if no label
      };
    }
  });

  // Add combined address if there are address fields
  if (addressValues.length > 0) {
    result.address = {
      value: addressValues.filter(Boolean).join(", "),
      type: "address",
      label: t("Address"),
    };
  }

  return result;
};

export const PaymentMethodPreview: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    getAllFormData,
    paymentMethod,
    submitFormData,
    isSubmitting,
    acknowledgedLegalDocumentIds,
    toggleLegalDocumentAcknowledged,
    allLegalDocumentsAcknowledged,
  } = usePaymentMethodForms();

  const allFormData = getAllFormData();

  if (!paymentMethod) {
    return null;
  }

  const groupedFormData = groupAddressFields(allFormData, paymentMethod, t);

  const handleSubmit = async () => {
    await submitFormData();
  };

  const getIsDisabled = () => isSubmitting || !allLegalDocumentsAcknowledged;

  return (
    <CardContainer showBorder={true}>
      <CardContainer.Header>
        <CardContainer.HeaderContent>
          <CardContainer.CardTitle>
            {paymentMethod?.title ?? t("mileageReview_PaymentMethod")}
          </CardContainer.CardTitle>
          <CardContainer.CardDescription>
            {t("auth_accountActivation_recheck_description")}
          </CardContainer.CardDescription>
        </CardContainer.HeaderContent>
      </CardContainer.Header>
      <CardContainer.Content>
        <ul>
          {Object.entries(groupedFormData).map(
            ([field, { value, type, label }], index, array) => {
              if (!value) return null;
              return (
                <React.Fragment key={field}>
                  <li
                    className="flex items-center py-2 w-full"
                    aria-label={field}
                  >
                    <div className="min-w-10 inline-flex shrink-0 text-muted-foreground">
                      {getFieldIcon(field as FieldTypes, type as ClientType)}
                    </div>
                    <div className="flex-1 flex-basis-auto min-w-0 py-1">
                      <p className="mb-1">{value}</p>

                      <p className="text-sm text-muted-foreground">
                        {formatFieldLabel(label)}
                      </p>
                    </div>
                  </li>
                  {index < array.length - 1 && <Separator className="my-2" />}
                </React.Fragment>
              );
            }
          )}
        </ul>

        <LegalDocumentsAcknowledgement
          documents={paymentMethod.legal_documents}
          acknowledgedIds={acknowledgedLegalDocumentIds}
          onToggle={toggleLegalDocumentAcknowledged}
        />

        <Button
          className="w-full mt-4"
          variant="outline"
          size="lg"
          onClick={() =>
            navigate(
              `/payment-methods/${paymentMethod.method_provider_name}/create`
            )
          }
          disabled={isSubmitting}
        >
          {t("virtualCard_summary_editButton")}
        </Button>

        <Button
          className="w-full mt-4"
          size="lg"
          loading={isSubmitting}
          onClick={handleSubmit}
          disabled={getIsDisabled()}
        >
          {t("continue_text")}
        </Button>
      </CardContainer.Content>
    </CardContainer>
  );
};
